import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

/**
 * PR-3790 — server-side paging, search and sort for the group pickers.
 * Opt-in: inert unless the caller supplies `fetchPage`, so un-migrated screens are unaffected.
 */

/** Fallback only: the server picks the real size (Web.config Groups.PickerPageSize) and returns it. */
export const PAGE_SIZE = 500;

/** How long to wait after the last keystroke before asking the server. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Load the next page once the 5th-from-last loaded row is visible (~one screen early), so it arrives before the end. */
export const PREFETCH_ROWS = 10;

/** Sort directions, matching the OrderBy enum on the server. */
export const SORT_DESC = 0;
export const SORT_ASC = 1;

/** The picker's sort labels are display strings; the SP wants column names. */
const SORT_FIELD_BY_LABEL: { [label: string]: string } = {
	'Group Name': 'GroupName',
	'Creation Date': 'CreationDate',
	'Update Date': 'UpdateDate',
};

export const toSortByField = (label: string): string =>
	SORT_FIELD_BY_LABEL[label] || 'GroupName';

export type PagedGroupsParams = {
	pageIndex: number;
	pageSize: number;
	searchTerm: string;
	showTestGroups: boolean;
	showEmptyGroups: boolean;
	sortByField: string;
	sortDirection: number;
};

export type PagedGroupsResult = {
	groups: any[];
	/** Total matching rows. -1 means "unchanged, keep what you have" (computed on page 1 only). */
	recordCount: number;
	/** Page size the server actually used; decides when the list has ended. */
	pageSize?: number;
};

export type PagedGroupsFilter = {
	searchTerm: string;
	showTestGroups: boolean;
	showEmptyGroups: boolean;
};

export type UseServerPagedGroupsOptions = {
	/** Supplying this turns paging on. Omit it for in-memory behaviour. */
	fetchPage?: (params: PagedGroupsParams) => Promise<PagedGroupsResult>;
	/** Controlled by the picker's "show test groups" button. */
	showTestGroups?: boolean;
	/** Whether groups with no recipients should be listed. */
	showEmptyGroups?: boolean;
	/** Sort label as shown in the dropdown, e.g. "Group Name". */
	sortBySelected?: string;
	/** 'asc' | 'desc', as the pickers already model it. */
	sortDirection?: string;
	pageSize?: number;
	/** Change this value to force a re-fetch from page 1, e.g. after a group is
	 *  created or clients are uploaded into one. */
	reloadToken?: number;
};

export type UseServerPagedGroupsResult = {
	enabled: boolean;
	rows: any[];
	recordCount: number;
	isLoading: boolean;
	hasMore: boolean;
	/** Raw value for the controlled search input. */
	searchInput: string;
	/** Debounced value actually sent to the server. */
	searchTerm: string;
	filter: PagedGroupsFilter;
	onSearchChange: (value: string) => void;
	resetSearch: () => void;
	loadMore: () => void;
	/** Re-fetch from page 1, e.g. after a new group is created. */
	reload: () => void;
};

export const useServerPagedGroups = ({
	fetchPage,
	showTestGroups = false,
	showEmptyGroups = false,
	sortBySelected = 'Update Date',
	sortDirection = 'desc',
	pageSize = PAGE_SIZE,
	reloadToken = 0,
}: UseServerPagedGroupsOptions): UseServerPagedGroupsResult => {
	const enabled = typeof fetchPage === 'function';

	const [rows, setRows] = useState<any[]>([]);
	const [recordCount, setRecordCount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [searchInput, setSearchInput] = useState<string>('');
	const [searchTerm, setSearchTerm] = useState<string>('');

	// Only the most recent request may commit, so a slow response cannot overwrite a newer one.
	const requestToken = useRef<number>(0);
	// A ref, not state: the scroll handler must see the new value without waiting for a render.
	const nextPageIndex = useRef<number>(1);
	const loadingRef = useRef<boolean>(false);

	const sortByField = toSortByField(sortBySelected);
	const sortDirectionValue = sortDirection === 'asc' ? SORT_ASC : SORT_DESC;

	const filter = useMemo(
		() => ({ searchTerm, showTestGroups, showEmptyGroups }),
		[searchTerm, showTestGroups, showEmptyGroups]
	);

	const debouncedSetSearchTerm = useMemo(
		() => debounce((value: string) => setSearchTerm(value), SEARCH_DEBOUNCE_MS),
		[]
	);

	useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

	const onSearchChange = useCallback(
		(value: string) => {
			setSearchInput(value);
			debouncedSetSearchTerm(value);
		},
		[debouncedSetSearchTerm]
	);

	const resetSearch = useCallback(() => {
		debouncedSetSearchTerm.cancel();
		setSearchInput('');
		setSearchTerm('');
	}, [debouncedSetSearchTerm]);

	const runFetch = useCallback(
		async (pageIndex: number, replace: boolean) => {
			if (!fetchPage) return;
			if (loadingRef.current) return;

			loadingRef.current = true;
			setIsLoading(true);

			const token = ++requestToken.current;

			try {
				const result = await fetchPage({
					pageIndex,
					pageSize,
					searchTerm,
					showTestGroups,
					showEmptyGroups,
					sortByField,
					sortDirection: sortDirectionValue,
				});

				// A newer request started while this one was in flight.
				if (token !== requestToken.current) return;

				const page = Array.isArray(result?.groups) ? result.groups : [];

				setRows((current) => (replace ? page : current.concat(page)));
				const usedPageSize =
					typeof result?.pageSize === 'number' && result.pageSize > 0 ? result.pageSize : pageSize;
				setHasMore(page.length >= usedPageSize);
				nextPageIndex.current = pageIndex + 1;

				// -1 means the server did not recompute the total for this page.
				if (typeof result?.recordCount === 'number' && result.recordCount >= 0) {
					setRecordCount(result.recordCount);
				}
			} catch (error) {
				if (token !== requestToken.current) return;
				// Stop paging rather than looping on a failure; keep the rows we have.
				setHasMore(false);
				if (replace) setRows([]);
			} finally {
				if (token === requestToken.current) {
					setIsLoading(false);
				}
				loadingRef.current = false;
			}
		},
		[
			fetchPage,
			pageSize,
			searchTerm,
			showTestGroups,
			showEmptyGroups,
			sortByField,
			sortDirectionValue,
		]
	);

	// Any change to the filter or the sort starts the list again from page 1.
	useEffect(() => {
		if (!enabled) return;
		requestToken.current++;
		nextPageIndex.current = 1;
		loadingRef.current = false;
		setHasMore(true);
		runFetch(1, true);
		// runFetch already depends on every input that should trigger a reset.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, searchTerm, showTestGroups, showEmptyGroups, sortByField, sortDirectionValue, reloadToken]);

	const loadMore = useCallback(() => {
		if (!enabled || loadingRef.current || !hasMore) return;
		runFetch(nextPageIndex.current, false);
	}, [enabled, hasMore, runFetch]);

	const reload = useCallback(() => {
		if (!enabled) return;
		requestToken.current++;
		nextPageIndex.current = 1;
		loadingRef.current = false;
		setHasMore(true);
		runFetch(1, true);
	}, [enabled, runFetch]);

	return {
		enabled,
		rows,
		recordCount,
		isLoading,
		hasMore,
		searchInput,
		searchTerm,
		filter,
		onSearchChange,
		resetSearch,
		loadMore,
		reload,
	};
};

export default useServerPagedGroups;
